// Sidebar hover fix 2026-10-25 (Val, Safari on a Mac: "Sidebar does not unhide on hover").
//
// Which kind of left menu a window gets. Before this fix the slim icon rail (and hover-to-open) only existed at 1024px and
// wider, so a Safari window at about 1000px - half of a wide screen - got the phone drawer instead: no rail to rest the
// pointer on, so nothing could ever open on hover. Now:
//   - a mouse or trackpad (hover: hover + pointer: fine) and a window at least 480px wide  -> desktop rail, hover opens it
//     (was 768px; Val's half-screen Safari window is about 750px, so it still got the drawer - fixed 2026-10-25 1:50 AM)
//   - any window at least 1024px wide (even touch-only)                                    -> desktop rail, tap the ☰ to open
//   - everything else (phones, touch tablets under 1024px, mouse windows under 480px)                                 -> the tap drawer, as before
// CRMApp reads the same answer to decide whether the header needs its own ☰ button.
import { useEffect, useState } from 'react';

export const NAV_RAIL_MIN_WIDTH = 480;
export const NAV_WIDE_MIN_WIDTH = 1024;
export const FINE_HOVER_QUERY = '(hover: hover) and (pointer: fine)';
const RAIL_QUERY = `(min-width: ${NAV_RAIL_MIN_WIDTH}px)`;
const WIDE_QUERY = `(min-width: ${NAV_WIDE_MIN_WIDTH}px)`;

function matches(query, fallback) {
    try {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return fallback;
        const result = window.matchMedia(query);
        return result ? Boolean(result.matches) : fallback;
    } catch {
        return fallback;
    }
}

/** True when this device has a mouse or trackpad that can hover. No matchMedia (old test DOMs) counts as yes. */
export function hasFineHover() {
    return matches(FINE_HOVER_QUERY, true);
}

/** True when the window should show the desktop icon rail rather than the phone drawer. */
export function isDesktopNav() {
    const width = typeof window === 'undefined' ? NAV_WIDE_MIN_WIDTH : window.innerWidth;
    const wide = matches(WIDE_QUERY, width >= NAV_WIDE_MIN_WIDTH);
    const railWide = matches(RAIL_QUERY, width >= NAV_RAIL_MIN_WIDTH);
    return wide || (railWide && hasFineHover());
}

/** Live version of isDesktopNav(): re-checks when the window is resized or a mouse is plugged in or removed. */
export function useDesktopNav(onChange) {
    const [desktop, setDesktop] = useState(() => isDesktopNav());
    useEffect(() => {
        const queries = typeof window.matchMedia === 'function'
            ? [WIDE_QUERY, RAIL_QUERY, FINE_HOVER_QUERY].map((query) => window.matchMedia(query)).filter(Boolean)
            : [];
        let current = isDesktopNav();
        setDesktop(current);
        const update = () => {
            const next = isDesktopNav();
            if (next === current) return;
            current = next;
            setDesktop(next);
            onChange?.(next);
        };
        queries.forEach((query) => query.addEventListener?.('change', update));
        window.addEventListener('resize', update);
        return () => {
            queries.forEach((query) => query.removeEventListener?.('change', update));
            window.removeEventListener('resize', update);
        };
    }, [onChange]);
    return desktop;
}
